import { FC, useEffect, useRef, useState } from "react";
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";
import {
  RangeBox,
  RangeText,
  RangeInputs,
  RangeInputWrapper,
} from "./RangeSlider.styled";
import { Input } from "../../Input/Input";
import { prepareRangeValueChange, rangeElement } from "./helper";
import { useDebounce } from "../../../hooks/useDebounce";

type Timer = ReturnType<typeof setTimeout>;

export interface RangeDataInfo {
  value: [number, number];
}

type Props = {
  value: RangeDataInfo;
  onChange: (value: unknown) => void;
  min?: number;
  max?: number;
  step?: number;
  range: boolean;
  isEditing?: boolean;
};

export const RangeSliderBtn: FC<Props> = ({
  value: { value },
  onChange,
  min,
  max,
  range,
  step,
  isEditing,
}) => {
  const [triggerToUpdate, setTriggerToUpdate] = useState<boolean>(false);

  const ref = useRef(null);
  const triggerTimer = useRef<Timer>();
  const debounceUpdateValues = useDebounce(updateValues, 700);

  const updateTrigger = () => {
    setTriggerToUpdate(true);

    clearTimeout(triggerTimer.current);
    triggerTimer.current = setTimeout(() => {
      setTriggerToUpdate(false);
    }, 500);
  };

  function updateValues(inputValue: number, element: rangeElement) {
    let localValue = inputValue;

    // Робимо перевірку на максимальне / мінімальне значення
    // Та на перекреслення значень

    if (typeof min === "number" && typeof max === "number") {
      localValue = prepareRangeValueChange({
        value: localValue,
        min,
        max,
        currentValues: value,
        element,
        range,
        step,
      });
    }

    if (element === "first") {
      onChange({ value: [localValue, value[1]] });
    }

    if (element === "second" || element === "alone") {
      onChange({ value: [value[0], localValue] });
    }

    updateTrigger();
  }

  useEffect(() => {
    if (!ref.current) return;
    const optionUpper = (ref.current as any).element.current.querySelector(
      "[data-upper]"
    );
    const optionLower = (ref.current as any).element.current.querySelector(
      "[data-lower]"
    );

    if (optionUpper && value)
      (
        optionUpper as HTMLElement
      ).innerHTML = `<div class='range-slider_text' data-index="1">${value[1]}</div>`;

    if (optionLower)
      (optionLower as HTMLElement).innerHTML = range
        ? `<div class='range-slider_text' data-index="0">${value[0]}</div>`
        : ``;
  }, [ref, range, value]);

  useEffect(() => {
    if (!ref.current) return;
    const labels = (ref.current as any).element.current.querySelectorAll(
      ".range-slider_text"
    ) as HTMLElement[];
    if (labels && value)
      for (let el of labels) {
        el.innerText = `${value[+(el.dataset.index ?? 0)]}`;
      }
  }, [ref, value]);

  return (
    <RangeBox
      className={`${isEditing ? "read-only" : ""} ${range ? "is-range" : ""}`}
    >
      <RangeInputs>
        {range ? (
          <>
            <RangeInputWrapper>
              <Input
                type="number"
                value={{ value: value[0] }}
                onChange={(value) =>
                  debounceUpdateValues(Number(value), "first")
                }
                min={min}
                max={max}
                triggerToUpdate={triggerToUpdate}
              />
            </RangeInputWrapper>
            <span className="decor-line">-</span>
            <RangeInputWrapper>
              <Input
                type="number"
                value={{ value: value[1] }}
                onChange={(value) =>
                  debounceUpdateValues(Number(value), "second")
                }
                min={min}
                max={max}
                triggerToUpdate={triggerToUpdate}
              />
            </RangeInputWrapper>
          </>
        ) : (
          <RangeInputWrapper>
            <Input
              type="number"
              value={{ value: value[1] }}
              onChange={(value) => debounceUpdateValues(Number(value), "alone")}
              min={min}
              max={max}
              triggerToUpdate={triggerToUpdate}
            />
          </RangeInputWrapper>
        )}
      </RangeInputs>
      <RangeSlider
        className={range ? "" : "single-thumb"}
        ref={ref}
        min={min}
        max={max}
        step={step}
        value={value}
        thumbsDisabled={[!range, false]}
        rangeSlideDisabled={!range}
        onInput={(value: [number, number]) => onChange({ value })}
      />

      <RangeText>
        <span>{min}</span>
        <span>{max}</span>
      </RangeText>
    </RangeBox>
  );
};
